import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { graphql } from "react-relay";
import { components, type OptionProps, type SingleValueProps } from "react-select";

import type { OkrSelectorOption } from ".";
import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faBullseyeArrow, faSlidersH } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ConnectionHandler } from "relay-runtime";

import AppContext from "~/common/AppContext";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import type { ObjectiveAttributes } from "~/okrs/Objective";
import UpdateDescription from "~/okrs/Updates/UpdateDescription";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { IconButton } from "~/ui/legacyFellowIndex";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";
import { condensedStyles } from "~/ui/selectors/SelectorUtils";
import toast from "~/ui/toast";

import type { AddNewUpdateMutation } from "~/__generated__/AddNewUpdateMutation.graphql";

import styles from "./AddNewUpdate.module.css";

interface AddNewUpdateProps {
	objectiveId: string;
	objectiveTitle: string;
	contributors: ObjectiveAttributes["contributors"];
	viewerId: string;
	keyResults: ObjectiveAttributes["keyResults"];
	isAddingDescription: boolean;
	setIsAddingDescription: (isAddingDescription: boolean) => void;
	selectedOkr: SelectorOption;
	setSelectedOkr: (selectedOkr: SelectorOption) => void;
	filteredOkr: OkrSelectorOption;
	setFilteredOkr: (filteredOkr: OkrSelectorOption) => void;
}

const addNewUpdate = graphql`
	mutation AddNewUpdateMutation($objectiveOrKrId: ID!, $text: String!, $json: JSON!, $connections: [ID!]!) {
		Objective_CreateFloatingDescription(objectiveOrKrId: $objectiveOrKrId, text: $text, json: $json) {
			update @appendNode(connections: $connections, edgeTypeName: "ActivityEdge") {
				id
				__typename
				actor {
					id
					fullName
					image
					email
				}
				verb
				createdAt
				cache
				data
			}
		}
	}
`;

function OkrFilterSingleValue({ ...rest }: SingleValueProps<OkrSelectorOption, false>) {
	return (
		<components.SingleValue {...rest}>
			<FontAwesomeIcon icon={faSlidersH} />
		</components.SingleValue>
	);
}

function OkrOption({ children, innerProps, ...rest }: OptionProps<SelectorOption, false>) {
	// The ID attribute has form react-select-5-option-<n>
	// We always add the objective first, so the innerProps ID will end with 0 for the objective option
	const isObjectiveOption = innerProps.id?.endsWith("0");

	return (
		<components.Option {...rest} innerProps={{ ...innerProps }}>
			{isObjectiveOption ? (
				<FontAwesomeIcon icon={faBullseyeArrow} width="16px" className={styles.bullseye} />
			) : (
				<div className={styles.vector}></div>
			)}
			<div title={children?.toString()} className={styles.optionText}>
				{children}
			</div>
		</components.Option>
	);
}

export default function AddNewUpdate({
	objectiveId,
	objectiveTitle,
	viewerId,
	contributors,
	keyResults,
	isAddingDescription,
	setIsAddingDescription,
	selectedOkr,
	setSelectedOkr,
	filteredOkr,
	setFilteredOkr,
}: AddNewUpdateProps) {
	const { user } = useContext(AppContext);
	const [loading, setLoading] = useState<boolean>(false);

	const objectiveSelectorOption = { label: objectiveTitle, value: objectiveId, isObjective: true };

	useEffect(() => {
		if (selectedOkr.value === objectiveId && selectedOkr.label !== objectiveTitle) {
			setSelectedOkr({ label: objectiveTitle, value: objectiveId });
		} else {
			const keyResult = keyResults.find(keyResult => keyResult.id === selectedOkr.value);
			if (keyResult && keyResult.title !== selectedOkr.label) {
				setSelectedOkr({ label: keyResult.title, value: keyResult.id });
			}
		}
	}, [objectiveId, objectiveTitle, keyResults, selectedOkr, setSelectedOkr]);

	const connectionId = useMemo(
		() =>
			ConnectionHandler.getConnectionID(viewerId, "updates_updates", {
				outerObjectId: objectiveId,
			}),
		[viewerId, objectiveId],
	);

	const onAddUpdate = useCallback(() => {
		setIsAddingDescription(true);
	}, [setIsAddingDescription]);

	const onSendUpdate = useCallback(
		(text: string, json: {}) => {
			setLoading(true);
			commitMutationPromise<AddNewUpdateMutation>(environment, {
				mutation: addNewUpdate,
				variables: {
					objectiveOrKrId: selectedOkr.value,
					text,
					json,
					connections: [connectionId],
				},
				onCompleted: () => {
					setLoading(false);
					setIsAddingDescription(false);
				},
				onError: () => {
					toast.error("Failed to create new update", { title: "Error" });
					setLoading(false);
				},
			});
		},
		[setLoading, selectedOkr, setIsAddingDescription, connectionId],
	);

	const updateSelectedOkr = useCallback(
		(val: SingleValue<SelectorOption>) => {
			if (!val) return;
			setSelectedOkr(val);
		},
		[setSelectedOkr],
	);

	const updateFilteredOkr = useCallback(
		(val: SingleValue<SelectorOption>) => {
			if (!val) return;
			setFilteredOkr(val);
			setSelectedOkr(val);
		},
		[setFilteredOkr, setSelectedOkr],
	);

	const keyResultOptions = keyResults
		.filter(keyResult => keyResult.title)
		.map(keyResult => ({
			label: keyResult.title,
			value: keyResult.id,
			isObjective: false,
		}));

	const selectorOptions = [objectiveSelectorOption].concat(keyResultOptions);

	const closeCommentEditor = useCallback(() => {
		setIsAddingDescription(false);
	}, [setIsAddingDescription]);

	if (!isAddingDescription) {
		return (
			<div className={styles.commentButtonFilterContainer}>
				<Button variant="secondary" size="wide" onClick={onAddUpdate} className={styles.newUpdateButton}>
					Add a comment
				</Button>
				<Selector
					condensed
					className={styles.okrFilterTrigger}
					value={filteredOkr}
					onChange={updateFilteredOkr}
					options={selectorOptions}
					components={{ Option: OkrOption, SingleValue: OkrFilterSingleValue, IndicatorsContainer: () => null }}
					styles={{
						menu: base => ({
							// @ts-ignore
							...condensedStyles.menu(base),
							width: "18.625rem",
							right: 0,
						}),
						option: (base, state) => ({
							// @ts-ignore
							...condensedStyles.option(base, state),
							whiteSpace: "normal",
						}),
						control: (base, state) => ({
							// @ts-ignore
							...condensedStyles.control(base, state),
							borderRadius: "50%",
						}),
						valueContainer: base => ({
							// @ts-ignore
							...condensedStyles.valueContainer(base),
							padding: 0,
							margin: "0.25rem",
							fontSize: "1rem",
						}),
						container: base => ({
							// @ts-ignore
							...base,
							marginRight: "0.5rem",
						}),
					}}
				/>
			</div>
		);
	}

	return (
		<div className={styles.addUpdate}>
			<div className={styles.userInfo}>
				<Avatar user={user} width="1.5rem" />
				{user?.fullName}
				<IconButton icon={faTimes} className={styles.closeCommentEditor} onClick={closeCommentEditor} />
			</div>
			<Selector
				condensed
				value={selectedOkr}
				onChange={updateSelectedOkr}
				options={selectorOptions}
				components={{ Option: OkrOption }}
				styles={{
					menu: base => ({
						// @ts-ignore
						...condensedStyles.menu(base),
						width: "18.625rem",
					}),
					option: (base, state) => ({
						// @ts-ignore
						...condensedStyles.option(base, state),
						whiteSpace: "normal",
					}),

					menuList(base) {
						return {
							// @ts-ignore
							...condensedStyles.menuList(base),
							maxWidth: "18.625rem",
						};
					},

					control(base, props) {
						return {
							// @ts-ignore
							...condensedStyles.control(base, props),
							maxWidth: "18.625rem",
						};
					},

					valueContainer(base) {
						return {
							// @ts-ignore
							...condensedStyles.valueContainer(base),
							maxWidth: "100%",
						};
					},
				}}
			/>
			<UpdateDescription contributors={contributors} onSubmit={onSendUpdate} disabled={loading} />
		</div>
	);
}
